import { inject, Injectable } from '@angular/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { ColDef } from 'ag-grid-community/dist/lib/entities/colDef';

import { FormlyHideExpression, FormlyProps } from 'tms-lib';
import { Address, CustomerAddress, SupplierAddress } from '../../models';
import { AddressService } from '../edit-create/address/address.service';
import { CustomerAddressService } from '../edit-create/sales/customer-address.service';
import { ManifestDeliveryAddressService } from '../edit-create/sales/manifest-delivery-address.service';
import { SupplierAddressService } from '../edit-create/purchasing/supplier-address.service';
import { AddressCreateModalComponent } from '../../components/address/address-create-modal/address-create-modal.component';
import { CustomerAddressCreateModalComponent } from '../../components/address/customer-address-create-modal/customer-address-create-modal.component';
import { SupplierAddressCreateModalComponent } from '../../components/address/supplier-address-create-modal/supplier-address-create-modal.component';
import { TmsFormlyService } from './tms-formly.service';

@Injectable({
  providedIn: 'root',
})
export class TmsFormlyAddressSearchService extends TmsFormlyService {
  private readonly addressService = inject(AddressService);
  private readonly customerAddressService = inject(CustomerAddressService);
  private readonly supplierAddressService = inject(SupplierAddressService);
  private readonly manifestDeliveryAddressService = inject(ManifestDeliveryAddressService);

  readonly addressSearchGridColumnDefs: ColDef[] = [
    this.gridService.text('accountNo', $localize`Account Number`),
    this.gridService.text('addressee', $localize`Addressee`),
    this.gridService.text('name', $localize`Name`),
    this.gridService.text('line1', $localize`Line 1`),
    this.gridService.text('line2', $localize`Line 2`),
    this.gridService.text('line3', $localize`Line 3`),
    this.gridService.text('line4', $localize`Line 4`),
    this.gridService.text('townCity', $localize`Town/City`),
    this.gridService.text('region', $localize`Region`),
    this.gridService.text('postCode', $localize`Postcode`),
    this.gridService.text('countryName', $localize`Country`),
    this.gridService.text('phone', $localize`Telephone`),
    this.gridService.text('email', $localize`Email`),
    this.gridService.text('vatRegNo', $localize`VAT Number`),
  ];

  addressSearch(
    key: string,
    multiLineKey: string,
    label: string,
    id: (model: any) => any,
    selected: (model: Address) => void,
    clear: () => void,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
  ): FormlyFieldConfig[] {
    return [
      this.modalSearch(
        key,
        label ?? $localize`Address`,
        this.addressService,
        this.addressSearchGridColumnDefs,
        selected,
        clear,
        props,
        hideExpression,
        id,
        undefined,
        $localize`All Addresses`,
        AddressCreateModalComponent,
      ),
      this.textarea(
        multiLineKey,
        label,
        {
          ...props,
          readonly: true,
          required: false,
          tabindex: -1,
        },
        hideExpression,
      ),
    ];
  }

  customerAddressSearch(
    key: string,
    multiLineKey: string,
    label: string,
    id: (model: any) => any,
    selected: (model: CustomerAddress) => void,
    clear: () => void,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
    allLabel = $localize`All Addresses`,
  ): FormlyFieldConfig[] {
    return [
      this.modalSearch(
        key,
        label ?? $localize`Customer Address`,
        this.customerAddressService,
        this.addressSearchGridColumnDefs,
        selected,
        clear,
        props,
        hideExpression,
        id,
        undefined,
        allLabel,
        CustomerAddressCreateModalComponent,
      ),
      this.textarea(
        multiLineKey,
        this.menuService.isReadOnly ? label : '',
        {
          ...props,
          readonly: true,
          required: false,
          tabindex: -1,
        },
        hideExpression,
      ),
    ];
  }

  /**
   * A manifest's delivery address: the manifest shipper's addresses (`shipperId`) as well as the
   * customer's (`customerId`) - see ManifestDeliveryAddressService. New addresses are customer addresses.
   */
  manifestDeliveryAddressSearch(
    key: string,
    multiLineKey: string,
    label: string,
    customerId: (model: any) => any,
    shipperId: (model: any) => any,
    selected: (model: CustomerAddress | SupplierAddress) => void,
    clear: () => void,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
  ): FormlyFieldConfig[] {
    return [
      this.modalSearch(
        key,
        label,
        this.manifestDeliveryAddressService,
        this.addressSearchGridColumnDefs,
        selected,
        clear,
        props,
        hideExpression,
        customerId,
        shipperId,
        $localize`All Addresses`,
        CustomerAddressCreateModalComponent,
      ),
      this.textarea(
        multiLineKey,
        this.menuService.isReadOnly ? label : '',
        {
          ...props,
          readonly: true,
          required: false,
          tabindex: -1,
        },
        hideExpression,
      ),
    ];
  }

  supplierAddressSearch(
    key: string,
    multiLineKey: string,
    label: string,
    id: (model: any) => any,
    selected: (model: SupplierAddress) => void,
    clear: () => void,
    props?: FormlyProps,
    hideExpression?: FormlyHideExpression,
  ): FormlyFieldConfig[] {
    return [
      this.modalSearch(
        key,
        label ?? $localize`Supplier Address`,
        this.supplierAddressService,
        this.addressSearchGridColumnDefs,
        selected,
        clear,
        props,
        hideExpression,
        id,
        undefined,
        $localize`All Addresses`,
        SupplierAddressCreateModalComponent,
      ),
      this.textarea(
        multiLineKey,
        this.menuService.isReadOnly ? label : '',
        {
          ...props,
          readonly: true,
          required: false,
          tabindex: -1,
        },
        hideExpression,
      ),
    ];
  }
}
